import { createAsync, query, A } from "@solidjs/router";
import { For, Show } from "solid-js";

// Rolling windows rather than calendar months: a month-to-date dashboard is
// empty on the 1st and only becomes meaningful late in the month. The monthly
// email keeps the calendar view.
const getSummary = query( async () => {
	"use server";

	const { loadStatsData, computeStats, rollingDays } = await import( "~/_server/stats" );

	const data = await loadStatsData();
	const now = Math.floor( Date.now() / 1000 );
	const DAY = 24 * 60 * 60;

	const current = computeStats( data, rollingDays( 30, now ) );
	const prior = computeStats( data, { from: now - 60 * DAY, to: now - 30 * DAY } );

	return {
		current: {
			registrations: current.registrations,
			assessmentPasses: current.assessmentPasses,
			newCertifiers: current.newCertifiers,
			certificatesIssued: current.certificatesIssued,
			active: current.totals.active,
			certifying: current.totals.certifying,
		},
		prior: {
			registrations: prior.registrations,
			assessmentPasses: prior.assessmentPasses,
			newCertifiers: prior.newCertifiers,
			certificatesIssued: prior.certificatesIssued,
			active: prior.totals.active,
			certifying: prior.totals.certifying,
		},
	};
}, "getStatsSummary" );

const Delta = ( props: { now: number, prev: number } ) => {
	const diff = () => props.now - props.prev;

	return (
		<Show when={diff() !== 0} fallback={<span class="text-[11px] text-gray-400">no change</span>}>
			<span class={`text-[11px] font-bold ${diff() > 0 ? "text-green-600" : "text-red-600"}`}>
				{diff() > 0 ? "▲" : "▼"} {Math.abs( diff() )}
			</span>
			<span class="text-[11px] text-gray-400"> vs prior 30</span>
		</Show>
	);
};

export const StatsSummary = () => {
	const data = createAsync( () => getSummary() );

	const cells = () => {
		const d = data();

		if ( !d ) return [];

		return [
			{ label: "Registrations", now: d.current.registrations, prev: d.prior.registrations },
			{ label: "Assessment passes", now: d.current.assessmentPasses, prev: d.prior.assessmentPasses },
			{ label: "New certifiers", now: d.current.newCertifiers, prev: d.prior.newCertifiers },
			{ label: "Certificates", now: d.current.certificatesIssued, prev: d.prior.certificatesIssued },
			{ label: "Certifying", now: d.current.certifying, prev: d.prior.certifying },
		];
	};

	return (
		<div class="white-box">
			<div class="flex items-baseline justify-between mb-4">
				<h2 class="text-xl text-black font-bold">Past 30 days</h2>
				<A href="/dashboard/admin/stats" class="text-[13px] text-sky-700 hover:underline">Full report →</A>
			</div>

			<Show when={data()} fallback={<p class="text-sm text-gray-500">Loading…</p>}>
				<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
					<For each={cells()}>
						{cell => (
							<div class="border border-gray-200 rounded-lg p-3">
								<p class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{cell.label}</p>
								<p class="text-3xl font-bold text-primary leading-tight">{cell.now}</p>
								<Delta now={cell.now} prev={cell.prev} />
							</div>
						)}
					</For>
				</div>
			</Show>
		</div>
	);
};
